import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';

// WabaContext — простой контекст выбранного instance для WABA-канала.
//
// В отличие от SelfQrContext, handoff не даёт endpoint listInstances() — каждый org
// настраивает credentials per instance_id вручную через CredentialsForm. Поэтому
// контекст хранит только currentInstanceId (number | null), не загружает список.
// Multi-instance UX (если orgs захотят несколько номеров) добавится при появлении
// backend-эндпоинта `GET /providers/waba/credentials` — см. координация п.1 в плане.
//
// Feature-flag и permission'ы НЕ дублируются здесь — доступ к модулю проверяется
// на уровне роута через <RequireFeature name="waba"> + <RequirePermission>, см. App.tsx.
//
// Multi-tab синхронизация (D-OV-7): при смене instance вкладка пишет в localStorage И
// broadcast'ит `instance_changed` на стабильном BroadcastChannel-имени (НЕ зависит от
// WS URL — иначе follower-вкладки слушают пустой канал после смены). Follower-вкладки
// перечитывают instance_id и пересоздают свой WS.

interface WabaContextType {
  currentInstanceId: number | null;
  setCurrentInstanceId: (id: number | null) => void;
}

const WabaContext = createContext<WabaContextType | null>(null);

const STORAGE_KEY = 'waba_selected_instance_id';
const BROADCAST_CHANNEL_NAME = 'waba_instance_sync';

const readStoredInstanceId = (): number | null => {
  if (typeof window === 'undefined') return null;
  const raw = safeGetItem(STORAGE_KEY);
  if (!raw) return null;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
};

interface BroadcastMessage {
  type: 'instance_changed';
  instanceId: number | null;
}

export const WabaProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [currentInstanceId, setCurrentInstanceIdState] = useState<number | null>(() =>
    readStoredInstanceId(),
  );
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Подписка на BroadcastChannel: follower-вкладки реагируют на instance_changed.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channelRef.current = channel;

    const onMessage = (event: MessageEvent<BroadcastMessage>) => {
      const msg = event.data;
      if (!msg || msg.type !== 'instance_changed') return;
      // Сверяем с localStorage — leader уже записал туда; не доверяем payload без подтверждения.
      const fromStorage = readStoredInstanceId();
      const next = fromStorage ?? msg.instanceId;
      setCurrentInstanceIdState((prev) => (prev === next ? prev : next));
    };

    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      channelRef.current = null;
    };
  }, []);

  const setCurrentInstanceId = useCallback((id: number | null) => {
    setCurrentInstanceIdState(id);
    if (id === null) {
      safeRemoveItem(STORAGE_KEY);
    } else {
      safeSetItem(STORAGE_KEY, String(id));
    }
    // Broadcast другим вкладкам — они пересоздадут свои WS на новый instance URL.
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({ type: 'instance_changed', instanceId: id });
      } catch {
        // BroadcastChannel может выкинуть DataCloneError на старых движках; игнор.
      }
    }
  }, []);

  const value = useMemo<WabaContextType>(
    () => ({
      currentInstanceId,
      setCurrentInstanceId,
    }),
    [currentInstanceId, setCurrentInstanceId],
  );

  return <WabaContext.Provider value={value}>{children}</WabaContext.Provider>;
};

export const useWaba = (): WabaContextType => {
  const v = useContext(WabaContext);
  if (!v) throw new Error('useWaba must be used within WabaProvider');
  return v;
};
